// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionship,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateRange,
  getPreviousRegionSeasonId,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
  isLatestRegionSeason,
} from 'common.helpers/dist/browser';
import {
  getLocalItemPath,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import Section from '../../../layout/Section';
import ShareButton from '../../../common/ShareButton';

// Enums
import {
  statusLabelClass,
} from '../../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $FullChampionship;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

const championshipStatus = {
  ended: 'ENDED',
  ongoing: 'ONGOING',
  upcoming: 'UPCOMING',
};

const championshipStatusLabel = {
  [championshipStatus.ended]: 'Ended',
  [championshipStatus.ongoing]: 'Ongoing',
  [championshipStatus.upcoming]: 'Upcoming',
};

class DetailsSectionView extends React.Component<$Props> {
  getDates(): {
    end?: Date;
    start?: Date ;
    } {
    const {
      item,
    } = this.props;

    const end: Date | undefined = _.get(
      _.maxBy(
        item.Regattas,
        'EndDate',
      ),
      'EndDate',
    );

    const start: Date | undefined = _.get(
      _.minBy(
        item.Regattas,
        'StartDate',
      ),
      'StartDate',
    );

    return {
      end,
      start,
    };
  }

  renderLogo() {
    const {
      item,
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: item.Image,
      size: 'max',
    });

    return (item.Image ? (
      <div className={'hidden md:block mr-5'}>
        <img
          alt={t('Logo')}
          className={'max-h-32 grow'}
          src={imageUrl}
        />
      </div>
    ) : null);
  }

  renderTitle() {
    const {
      item,
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: item.Image,
      size: 'mid',
    });

    return (
      <div className={'flex flex-row items-center'}>
        <div className={'md:hidden'}>
          {item.Image && (
            <img
              alt={t('Logo')}
              className={'flex-none w-20 mr-3'}
              src={imageUrl}
            />
          )}
        </div>
        <div className={'flex-1'}>
          <h1
            className={'leading-none'}
            title={t('Chmapionship title')}
          >
            <span className={'md:hidden h3'}>
              {item.Name}
            </span>
            <span className={'hidden md:block h1'}>
              {item.Name}
            </span>
          </h1>
        </div>
      </div>
    );
  }

  renderStatus() {
    const {
      t,
    } = this.props;

    const {
      end,
      start,
    } = this.getDates();

    const now: moment.Moment = moment();

    let status = championshipStatus.ongoing;

    if (end && now.isAfter(end)) {
      status = championshipStatus.ended;
    }

    if (start && now.isBefore(start)) {
      status = championshipStatus.upcoming;
    }

    return (
      <div className={`badge badge_outlined badge_${statusLabelClass[status]} uppercase py-1  `}>
        {t(championshipStatusLabel[status])}
      </div>
    );
  }

  renderDate() {
    const {
      item,
      main,
    } = this.props;

    const {
      end,
      start,
    } = this.getDates();

    const {
      RegionSeasonId,
    } = item;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const regionSeason = getRegionSeasonByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    return (
      <div className={'text-md sm:text-lg font-medium mr-3'}>
        {!isLatestRegionSeason({
          regions: main.regions,
          RegionSeasonId,
        }) && (
          <span>
            {regionSeason.Name}
            <span className={'text-gray-400'}>{' | '}</span>
          </span>
        )}
        {((end && start) ? formatDateRange({
          end,
          shortDateFormat: region.ShortDateFormat,
          start,
        }) : null)}
      </div>
    );
  }

  renderOrganizer() {
    const {
      item,
    } = this.props;

    return (item.OrganizerName ? (
      <div className={'mb-3'}>
        <a
          className={'text-gray-600'}
          href={`mailto:${item.OrganizerName || ''}<${item.OrganizerEmail}>?subject=${item.Name}`}
        >
          {item.OrganizerName || ''}
        </a>
      </div>
    ) : null);
  }

  renderReferenceButton() {
    const {
      item,
      main,
      t,
    } = this.props;

    const id = item.ReferenceId;

    if (id) {
      const previousRegionSeasonId = getPreviousRegionSeasonId({
        regions: main.regions,
        RegionSeasonId: item.RegionSeasonId,
      });

      if (previousRegionSeasonId !== null) {
        const itemUrl = getLocalItemPath({
          basePath: '/championship',
          item: {
            id,
            RegionSeasonId: previousRegionSeasonId,
          },
          regions: main.regions,
        });

        return (
          <a
            className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
            href={itemUrl}
            title={t('Previous year championship')}
          >
            <em className={'la la-history text-2xl'} />
            <span className={'hidden md:block ml-2'}>
              {t('Previous year championship')}
            </span>
          </a>
        );
      }
    }

    return null;
  }

  renderButtons() {
    return (
      <div className={'ml-2 sm:ml-5 flex flex-col justify-between'}>
        <div className={'flex flex-col md:flex-row'}>
          <div className={'mb-3 ml-auto'}>
            <ShareButton />
          </div>
        </div>
        {this.renderReferenceButton()}
      </div>
    );
  }

  render() {
    return (
      <Section id={webSections[webPages.championshipPage].details}>
        <div className={'flex flex-row'}>
          {this.renderLogo()}
          <div className={'grow'}>
            <div className={'mb-3'}>
              {this.renderTitle()}
            </div>
            {this.renderOrganizer()}
            <div className={'flex flex-row items-center'}>
              <div className={'py-auto mr-3'}>
                {this.renderStatus()}
              </div>
              {this.renderDate()}
            </div>
          </div>
          {this.renderButtons()}
        </div>
      </Section>
    );
  }
}

export default DetailsSectionView;
